/**
 * Cleanup Progress Provider
 *
 * Manages cleanup steps state with API integration and polling for OCU agent.
 * Automatically fetches steps when thread is created and polls for updates.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  useState,
} from 'react';
import { useAppSelector } from '@/stores/store';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { STORAGE_KEYS } from '@/config/storage-config';

/**
 * Number of retry attempts on failure
 */
const MAX_RETRY_ATTEMPTS = 3;

/**
 * Polling interval in milliseconds
 */
const POLLING_INTERVAL = 5000;

export interface CleanupStep {
  step_name: string;
  display_name: string;
  status: 'Created' | 'In progress' | 'Complete';
  issues_found: number;
  issues_resolved: number;
}

interface CleanupProgressContextValue {
  steps: CleanupStep[];
  isLoading: boolean;
  currentStep: CleanupStep | null;
  issuesFound: number;
  issuesResolved: number;
  scopeSelected: boolean;
  refetch: () => Promise<void>;
}

const defaultContextValue: CleanupProgressContextValue = {
  steps: [],
  isLoading: false,
  currentStep: null,
  issuesFound: 0,
  issuesResolved: 0,
  scopeSelected: false,
  refetch: async () => {},
};

const CleanupProgressContext = createContext<CleanupProgressContextValue>(defaultContextValue);

interface CleanupProgressProviderProps {
  children: ReactNode;
  threadId: string | null;
}

// Default cleanup steps for initial state - Order from PRD lines 243-515
const DEFAULT_CLEANUP_STEPS: CleanupStep[] = [
  { step_name: 'rapid_discovery', display_name: 'Rapid Discovery & Team Snapshot', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'services', display_name: 'Services', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'integrations', display_name: 'Integrations', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'incidents', display_name: 'Incidents', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'incident_workflows', display_name: 'Incident Workflows', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'aiops', display_name: 'AIOps', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'escalation_policies', display_name: 'Escalation Policies', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'schedules', display_name: 'Schedules', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'teams', display_name: 'Teams', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'responders', display_name: 'Responders', status: 'Created', issues_found: 0, issues_resolved: 0 },
  { step_name: 'stakeholders', display_name: 'Stakeholders', status: 'Created', issues_found: 0, issues_resolved: 0 },
];

export function CleanupProgressProvider({
  children,
  threadId,
}: CleanupProgressProviderProps) {
  const [steps, setSteps] = useState<CleanupStep[]>(DEFAULT_CLEANUP_STEPS);
  const [isLoading, setIsLoading] = useState(false);
  const [scopeSelected, setScopeSelected] = useState(false);
  const selectedAgent = useAppSelector(selectSelectedAgent);

  const retryCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch cleanup steps from API
   */
  const fetchSteps = useCallback(async () => {
    if (!threadId) {
      setSteps(DEFAULT_CLEANUP_STEPS);
      return;
    }

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      console.warn('No auth token available');
      return;
    }

    setIsLoading(true);

    try {
      const baseUrl = selectedAgent?.deploymentUrl || import.meta.env.VITE_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${baseUrl}/threads/${threadId}/cleanup-phases`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cleanup steps: ${response.status}`);
      }

      const data = await response.json();

      if (isMountedRef.current) {
        // If backend returns steps, use them; otherwise keep defaults
        if (data.steps && data.steps.length > 0) {
          setSteps(data.steps);
        }
        setScopeSelected(data.scope_selected || false);
        retryCountRef.current = 0;
      }
    } catch (error) {
      console.warn('Failed to fetch cleanup steps:', error);

      // Silent retry logic
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        retryCountRef.current += 1;
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchSteps();
          }
        }, 1000 * retryCountRef.current);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [threadId, selectedAgent]);

  // Fetch steps on mount and when threadId changes
  useEffect(() => {
    isMountedRef.current = true;
    fetchSteps();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchSteps]);

  // Set up polling for updates
  // TODO: TEMP disabled for backend testing — re-enable polling below
  // useEffect(() => {
  //   if (!threadId) return;

  //   pollingIntervalRef.current = setInterval(() => {
  //     if (isMountedRef.current) {
  //       fetchSteps();
  //     }
  //   }, POLLING_INTERVAL);

  //   return () => {
  //     if (pollingIntervalRef.current) {
  //       clearInterval(pollingIntervalRef.current);
  //     }
  //   };
  // }, [threadId, fetchSteps]);

  // Calculate derived values
  const currentStep = steps.find((step) => step.status === 'In progress') || null;
  const issuesFound = steps.reduce((sum, step) => sum + (step.issues_found || 0), 0);
  const issuesResolved = steps.reduce((sum, step) => sum + (step.issues_resolved || 0), 0);

  const contextValue: CleanupProgressContextValue = {
    steps,
    isLoading,
    currentStep,
    issuesFound,
    issuesResolved,
    scopeSelected,
    refetch: fetchSteps,
  };

  return (
    <CleanupProgressContext.Provider value={contextValue}>
      {children}
    </CleanupProgressContext.Provider>
  );
}

export function useCleanupProgress(): CleanupProgressContextValue {
  const context = useContext(CleanupProgressContext);
  if (!context) {
    throw new Error('useCleanupProgress must be used within a CleanupProgressProvider');
  }
  return context;
}
